import React, { useEffect, useState, useMemo } from "react"; // React and hooks for state and effects
import { Button, Col, Row } from "react-bootstrap"; // Bootstrap components for layout
import { useChatContext } from "../../../contexts/ChatContext"; // Custom hook for chat context
import useAutoScroll from "../../../hooks/useAutoScroll"; // Custom hook for auto-scrolling functionality
import ChatInput from "./ChatInputNew"; // Import the ChatInput component for user input
import ChatItemsDisplay from "./ChatItemsDisplay"; // Component to display chat items
import { pagesContent } from "../../../config/componentsConfig";
import { useRef } from "react";
import styles from "./Chat.module.scss";

// Define the props interface for the Chat component
interface ChatProps {
  filters: object; // Filters to apply to chat items
  setFilters: (filters: object) => void; // Function to set filters
  page: "market" | "legal" | "mydeal" | "oreima" | "home"; // Current page context
  selectedText: string; // Selected text
  selectedMode: string; // Selected mode
  selectedMenu: number; // Panneau latéral (5 = My Tasks) pour ne pas le fermer à la saisie
  setSelectedText: (text: string) => void; // Function to set selected text
  setSelectedMode: (mode: string) => void; // Function to set selected mode
  setSelectedMenu: (menu: number) => void;
  modeOptions: any[]; // Mode options
  setIsUpdatingFiles: (v: boolean) => void;
  devMode: boolean;
  setDevMode: (v: boolean) => void;
}

// Functional component for the chat interface
const Chat: React.FC<ChatProps> = ({
  filters,
  setFilters,
  page,
  selectedText,
  selectedMode,
  selectedMenu,
  setSelectedMenu,
  setSelectedText,
  setSelectedMode,
  modeOptions, 
  setIsUpdatingFiles,
  devMode,
  setDevMode,
}) => {
  const { clearChat, chatItems } = useChatContext(); // Get chat items from context and added clearChat
  const { isUserAtBottom, containerRef, bottomRef} = useAutoScroll(); // Scroll helpers (sans auto-scroll)
  const [displaySources, setDisplaySources] = useState(false);
  const [displayCarousel, setDisplayCarousel] = useState(false);
  const showDevButtons = true;
  const chatContainerRef = useRef(null);
  const [scrollBottomButtonPosition, setScrollBottomButtonPosition] = useState(0);

  // Initialisation / persistance de l’option d’affichage du carrousel par page.
  useEffect(() => {
    const key = `infravia:displayCarousel:${page}`;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw !== null) {
        setDisplayCarousel(raw === "true");
      }
    } catch {
      // ignore storage errors
    }
  }, [page]);

  useEffect(() => {
    const key = `infravia:displayCarousel:${page}`;
    try {
      window.localStorage.setItem(key, String(displayCarousel));
    } catch {
      // ignore
    }
  }, [displayCarousel, page]);

  // Effect for handling page change
  useEffect(() => {
    // Reset state when page changes
    clearChat();

    setSelectedText('');
    setSelectedMode("SEARCH");
    
    // Scroll to top of the conversation
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [page, clearChat, setSelectedText, setSelectedMode]);

  // Scroll to the bottom element
  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };


  // Function to update button position when scrolling
  const updatePosition = () => {
    if (containerRef.current) {
      setScrollBottomButtonPosition(containerRef.current.scrollTop);
    }
  };

  // Effect to update button position when scrolling
  useEffect(() => {
    const parent = containerRef.current;
    if (parent) {
      parent.addEventListener("scroll", updatePosition);
    }
    return () => {
      if (parent) {
        parent.removeEventListener("scroll", updatePosition);
      }
    };
  }, []);

  return (
    <Col className="d-flex flex-column h-100">
      <Row
        className={`flex-grow-1 overflow-y-scroll custom-sidebar g-0 pt-4 ${styles.chatScroll}`}
        style={{ position: "relative" }}
        ref={containerRef} // Reference for auto-scrolling
      >
        {!isUserAtBottom && (
          <Button 
          className={styles.scrollButton} 
          onClick={scrollToBottom}
          style={{ bottom: 10-scrollBottomButtonPosition, zIndex: 1000 }}
          >
            <i className="bi bi-chevron-down"></i>
          </Button>
          )}
        <div
          className="flex-fill d-flex flex-column justify-content-end px-3"
          style={{ minHeight: "100%" }} // Ensure minimum height for the chat area
          ref={chatContainerRef}
        >

          <ChatItemsDisplay
            displaySources={displaySources}
            displayCarousel={displayCarousel}
          /> {/* Display chat items */}
          <div ref={bottomRef} /> {/* Reference for scrolling to the bottom */}
        </div>
      </Row>
      <Row className="g-0 px-3">
        <ChatInput
          page={page}
          filters={filters} // Pass filters to ChatInput
          setFilters={setFilters} // Pass filters to ChatInput
          selectedText={selectedText} // Pass selected text to ChatInput
          selectedMode={selectedMode} // Pass selected mode to ChatInput
          selectedMenu={selectedMenu}
          setSelectedMenu={setSelectedMenu} //Pass to close menu when user types
          setSelectedMode={setSelectedMode} // Handle mode change
          modeOptions={modeOptions} // Pass the renamed mode options to ChatInput
          displaySources={displaySources}
          setDisplaySources={setDisplaySources}
          displayCarousel={displayCarousel}
          setDisplayCarousel={setDisplayCarousel}
          setIsUpdatingFiles={setIsUpdatingFiles}
          devMode={devMode}
          setDevMode={setDevMode}
          showDevButtons={showDevButtons}
        />
      </Row>
    </Col>
  );
};

export default Chat; // Export the Chat component
